import { useEffect, useState } from 'react';
import { SystemDialog } from '@ciancio/shared';
import { AdminAccessRequired } from '../components/users/AdminAccessRequired';
import { UserCreateForm } from '../components/users/UserCreateForm';
import { UserList } from '../components/users/UserList';
import { addUser, listUsers, removeUser, updateUserRole } from '../services/adminService';

export const UsersPage = ({ currentUser }) => {
  const [users, setUsers] = useState([]);
  const [form, setForm] = useState({ username: '', role: 'staff' });
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('manage');
  const [pendingRoleChange, setPendingRoleChange] = useState(null);

  useEffect(() => {
    listUsers()
      .then(setUsers)
      .finally(() => setLoading(false));
  }, []);

  const handleAdd = async (event) => {
    event.preventDefault();
    setMessage('');

    try {
      const result = await addUser(form);
      setUsers(result.users);
      setForm({ username: '', role: 'staff' });
      setActiveTab('manage');
      setMessage(result.passwordResetEmailSent
        ? `Created ${form.username}. Password reset email sent.`
        : `Created ${form.username}.`);
    } catch (error) {
      setMessage(error.message);
    }
  };

  const handleRemove = async (id) => {
    setMessage('');

    try {
      const nextUsers = await removeUser(id);
      setUsers(nextUsers);
    } catch (error) {
      setMessage(error.message);
    }
  };

  const handleRoleChange = (id, role) => {
    const user = users.find((item) => item.id === id);

    if (!user || user.role === role) {
      return;
    }

    setPendingRoleChange({ id, role, user });
  };

  const confirmRoleChange = async () => {
    const { id, role } = pendingRoleChange;
    setPendingRoleChange(null);

    setMessage('');

    try {
      const nextUsers = await updateUserRole(id, role);
      setUsers(nextUsers);
      setMessage('Role updated. The user must sign out and sign back in for the new role to apply.');
    } catch (error) {
      setMessage(error.message);
    }
  };

  if (currentUser.role !== 'admin') {
    return <AdminAccessRequired />;
  }

  return (
    <div className="px-3 pb-3">
      <div className="row m-0 gap-2 mb-3 rounded">
        <button
          className={activeTab === 'manage' ? 'btn bg-green col-12 col-md' : 'btn bg-beige col-12 col-md'}
          type="button"
          onClick={() => setActiveTab('manage')}
        >
          <i className="bi bi-people me-2" />
          Manage Users
        </button>
        <button
          className={activeTab === 'add' ? 'btn bg-green col-12 col-md' : 'btn bg-beige col-12 col-md'}
          type="button"
          onClick={() => setActiveTab('add')}
        >
          <i className="bi bi-person-plus me-2" />
          Add User
        </button>
      </div>

      {activeTab === 'add' ? (
        <UserCreateForm
          form={form}
          message={message}
          onChange={setForm}
          onSubmit={handleAdd}
        />
      ) : (
        loading ? (
          <div className="row p-4">
            <h4 className="text-center m-0">LOADING USERS</h4>
          </div>
        ) : (
          <UserList
            currentUser={currentUser}
            onRemove={handleRemove}
            onRoleChange={handleRoleChange}
            users={users}
          />
        )
      )}
      {pendingRoleChange && (
        <SystemDialog
          title="Change user role"
          confirmLabel="Change role"
          onCancel={() => setPendingRoleChange(null)}
          onConfirm={confirmRoleChange}
        >
          <p className="mb-0">Change <strong>{pendingRoleChange.user.username}</strong> from {pendingRoleChange.user.role} to {pendingRoleChange.role}?</p>
        </SystemDialog>
      )}
    </div>
  );
}

